import {faArrowLeft} from '@fortawesome/free-solid-svg-icons/faArrowLeft';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import gql from 'graphql-tag';
import {flowRight as compose} from 'lodash';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {Query, withApollo} from 'react-apollo';
import {Col, Container, Row} from 'react-bootstrap';
import {withTranslation} from 'react-i18next';
import {withRouter} from 'react-router';
import {Link} from 'react-router-dom';
import {PulseLoader} from 'react-spinners';
import StarRatings from 'react-star-ratings';
import styled from 'styled-components';
import config from '../../../config/app';
import {dateFormatFR} from '../../../utils';
import defaultBrand from '../../../common/assets/img/store.png';
import DataTable from '../../../common/components/Datatable/Datatable';
import DisplayTag from '../../../common/components/DisplayTag';
import Image from '../../../common/components/Image';
import DisplayInfo from '../../../common/components/layout/DisplayInfo';
import Screen from '../../../common/components/layout/Screen';
import Metadata from '../../../common/components/Metadata';
import {HeadDiv, Span} from '../../../common/components/StyledComp';
import {COLORS, FILTER_EXPRESSION_OPERATOR, FILTER_OPERATOR, ORDER_DIRECTION} from '../../../common/constants';
import RowActionReceiptUser from '../../ticket/components/RowActionReceiptUser';
import DisplayBoolean from '../../../common/components/DisplayBoolean';
import SelectReceiptIsPro from '../../../common/components/SelectReceiptIsPro';

const MargedDiv = styled.div`
  margin-right: 15px;
`;

const BrandName = styled.p`
  margin-left: 15px;
`;

const ImageMini = styled(Image)`
  height: 50px;
`;

export const SpanGoBack = styled.span`
  font-family: 'Segoe UI Bold', sans-serif;
  margin-left: 6px;
  margin-right: 10px;
  color: ${COLORS.DARK_PINK};
`;

export const GRAPHQL_QUERY_BRAND_DETAIL = gql`
  query brandDetail($brandId: ID!) {
    currentUser {
      id
      brand(id: $brandId) {
        id
        name
        receipts {
          id
          total
        }
        file {
          id
          key
          token
        }
        lastReceipt {
          id
          createdAt
        }
      }
    }
  }
`;


const GRAPHQL_QUERY_TICKETS_LIST = gql`
  query ticketsList($criteria: CriteriaInput!) {
    currentUser {
      id
      receipts(criteria: $criteria) {
        id
        total
        rows {
          id
          createdAt
          brand {
            id
            name
            file {
              id
              key
              token
            }
          }
          isPro
          rating
          comments {
            id
            createdAt
            review
            user {
              id
              firstName
              lastName
            }
          }
          tags {
            id
            rows {
              id
              name
            }
          }
          file {
            id
            key
            token
            filename
            mimeType
          }
          merchant{
            id
            businessName
            contactEmail
            phoneNumber
            user{
              id
              firstName
              lastName
              city
              postalCode
            }
          }
        }
      }
    }
  }
`;

class UserBrandDetails extends Component {

  state = {
    brandID: null,
    isReady: false,
    isPro: 'default',
    search: null,
  };

  onSearch = search => {
    this.setState({search});
  };

  buildFilters = (search, isPro, brandId) => {
    const filters = [
      {
        field: 'brandId',
        value: brandId,
        operator: FILTER_OPERATOR.Equal,
      },
    ];

    if (isPro !== 'default' && isPro !== 'all') {
      filters.push({
        field: 'receipt.isPro',
        operator: FILTER_OPERATOR.Equal,
        value: isPro,
      });
    }

    if (search) {
      filters.push({
        field: 'id',
        operator: FILTER_OPERATOR.Like,
        value: `%${search}%`,
      });
    }

    return {
      operator: FILTER_EXPRESSION_OPERATOR.And,
      filters,
    };
  };


  displayTags = (data) => {
    return (
      <div>
        {data.rows.map((tag, index) => (
          <DisplayTag key={index} tag={tag}/>
        ))}
      </div>
    );
  };

  displayAction = row => {
    return (
      <RowActionReceiptUser receipt={row}/>
    );
  };

  displayRating = data => {
    return (
      data ?
        <StarRatings
          rating={data || 0}
          starDimension="20px"
          starSpacing="3px"
          starRatedColor='#C90164'
        /> : null
    );
  };

  displayReviewStatus = data => {
    return (
      <DisplayBoolean bool={data !== undefined}/>
    );
  };

  columns = t => [
    {
      Header: '#',
      accessor: 'id',
    },
    {
      Header: t('dataTable.creation'),
      Cell: ({cell}) => dateFormatFR({date: cell.value}),
      accessor: 'createdAt',
      sortable: true,
    },
    {
      Header: t('dataTable.note'),
      Cell: ({cell}) => this.displayRating(cell.value),
      accessor: 'rating',
    },
    {
      Header: t('dataTable.comment'),
      Cell: ({cell}) => this.displayReviewStatus(cell.value),
      accessor: 'comments[0]',
    },
    {
      Header: t('dataTable.commentReply'),
      Cell: ({cell}) => this.displayReviewStatus(cell.value),
      accessor: 'comments[1]',
    },
    {
      Header: t('dataTable.isPro'),
      Cell: ({cell}) => <DisplayBoolean bool={cell.value}/>,
      accessor: 'isPro',
    },
    {
      Header: t('dataTable.tags'),
      Cell: ({cell}) => this.displayTags(cell.value),
      accessor: 'tags',
    },
    {
      Header: t('detail'),
      Cell: ({cell}) => this.displayAction(cell.value),
      accessor: row => row,
    },
  ];

  componentDidMount() {
    const {match} = this.props;
    const brandID = match.params.id || null;

    this.setState({
      brandID,
      advancedFilters:
        {
          filters: [
            {
              field: 'brandId',
              value: brandID,
              operator: FILTER_OPERATOR.Equal,
            },
          ],
        },
      isReady: true,
    });
  }

  render() {
    const {t, match} = this.props;

    const additionalFilters = (
      <MargedDiv>
        <SelectReceiptIsPro
          value={this.state.isPro}
          onChange={e => {
            this.setState({isPro: e.target.value});
          }}
        />
      </MargedDiv>
    );

    return (
      <Screen
        header={
          <Metadata meta={t('details')} title={t('details')}/>
        }
        leftTitle={
          <>
            <Link to={'/admin/brands/'} title={t('goBackToBrands')}>
              <SpanGoBack><FontAwesomeIcon icon={faArrowLeft} color={'rgb(201,0,95)'}/></SpanGoBack>
            </Link>
            <Span className="route-name">{t('details')}</Span>
          </>
        }>
        {!this.state.isReady ? (
          <PulseLoader loading={true} />
        ) : (
          <>
            <Query query={GRAPHQL_QUERY_BRAND_DETAIL} variables={{brandId : match.params.id || 0}}>
              {({data, loading, error}) => {
                if (loading) {
                  return <PulseLoader loading={true} />;
                }
                if (error) {
                  console.error(error);
                }
                if (data) {
                  const brandLogoUrl = data.currentUser.brand.file ? config.api_file_brand + data.currentUser.brand.id + '?token=' + data.currentUser.brand.file.token : defaultBrand;
                  return (
                    <Container className="general-container">
                      <Row className="info-row">
                        <Col lg={12} xl={6}>
                          <div className="section-info-user">
                            <HeadDiv className="info-header-user">{`${t('brand')} : `}</HeadDiv>
                            <Row className={'align-items-center flex-grow-1'}>
                              <Col xs={8}>
                                <BrandName>{data.currentUser.brand.name}</BrandName>
                              </Col>
                              <Col xs={4}>
                                <ImageMini
                                  className={'mb-2'}
                                  src={brandLogoUrl}
                                  alt={data.currentUser.brand.name}/>
                              </Col>
                            </Row>
                          </div>
                        </Col>
                        <Col lg={12} className={'d-xl-none'}>
                          <br/>
                        </Col>
                        <Col lg={12} xl={6}>
                          <div className="section-info-user">
                            <HeadDiv className="info-header-user">{`${t('tickets')} : `}</HeadDiv>
                            <DisplayInfo label={t('ticketsNb')} value={data.currentUser.brand.receipts.total}/>
                            <DisplayInfo
                              label={t('lastTicket')}
                              value={data.currentUser.brand.lastReceipt && dateFormatFR(data.currentUser.brand.lastReceipt.createdAt)}/>
                          </div>
                        </Col>
                      </Row>
                    </Container>
                  );
                }
                return null;
              }}
            </Query>

            <DataTable
              query={GRAPHQL_QUERY_TICKETS_LIST}
              keyResult="currentUser.receipts"
              columns={this.columns(t)}
              onSearch={this.onSearch}
              advancedFilters={this.buildFilters(this.state.search, this.state.isPro, this.state.brandID)}
              additionalFilters={additionalFilters}
              defaultSort={{field: 'createdAt', direction: ORDER_DIRECTION.DESC}}/>
          </>
        )}
      </Screen>
    );
  }
}

UserBrandDetails.propTypes = {
  t: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  client: PropTypes.object.isRequired,
  history: PropTypes.any.isRequired,
};

export default compose(
  withTranslation('ticket'),
  withRouter,
  withApollo,
)(UserBrandDetails);
